import { isNaN } from 'lodash';
import { IntlShape } from 'react-intl';

export const DateFormat = 'YYYY-mm-dd';
export const TimeFormat = 'HH:ii:ss';
export const DateTimeFormat = `${DateFormat}T${TimeFormat}Z`;

/*
const today = new Date();
today.setHours(0, 0, 0, 0); // Set today's time to midnight

// Calculate the date 150 years ago from today
new Date();
minDate.setFullYear(today.getFullYear() - 150);
minDate.setHours(0, 0, 0, 0); // Ensure only the date part is compared
*/
export const minBirthDate = new Date(
  new Date().setFullYear(new Date().getFullYear() - 150)
);
export const maxBirthDate = new Date().setHours(0, 0, 0, 0);

export const isValidDate = (dateString: string) => {
  const date = new Date(dateString);
  return !Number.isNaN(date.getTime());
};

export const parseTimeToMinutes = (timeStr: string): number | null => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

export const isValidBirthDate = (dateString: string) => {
  if (!isValidDate(dateString)) {
    return false; // Invalid date string
  }
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set today's time to midnight

  return date >= minBirthDate && date < today; // Check if the date is in range
};

export const formatDate = (
  value: Date | string | null | undefined,
  format = DateFormat
): string => {
  if (value) {
    const date = value instanceof Date ? value : new Date(value);

    if (date instanceof Date && !isNaN(date.getFullYear())) {
      const pad = (num: number, digits = 2) =>
        num.toString().padStart(digits, '0');

      const tzo = -date.getTimezoneOffset();

      return format
        .replace('YYYY', date.getFullYear().toString())
        .replace('mm', pad(date.getMonth() + 1))
        .replace('dd', pad(date.getDate()))
        .replace('HH', pad(date.getHours()))
        .replace('ii', pad(date.getMinutes()))
        .replace('ss', pad(date.getSeconds()))
        .replace(
          'Z',
          `${tzo >= 0 ? '+' : '-'}${pad(tzo / 60)}:${pad(tzo % 60)}`
        );
    }
  }

  return '';
};

export const formatTime = (value: Date | string | null | undefined): string =>
  formatDate(value, TimeFormat);

export const getAge = (
  value: Date | string | null | undefined
): number | undefined => {
  if (value) {
    const date = value instanceof Date ? value : new Date(value);

    if (date instanceof Date && !isNaN(date.getFullYear())) {
      const current = new Date();

      let age = current.getFullYear() - date.getFullYear();

      const monthDiff = current.getMonth() - date.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && current.getDate() < date.getDate())
      ) {
        age -= 1;
      }

      return age;
    }
  }

  return undefined;
};

export const getRelativeTime = (
  value: Date | string,
  locale: string,
  style: 'long' | 'short' | 'narrow' = 'long'
): string | undefined => {
  const date = value instanceof Date ? value : new Date(value);

  if (date instanceof Date) {
    const now = new Date();

    const msPerMinute = 60 * 1000;
    const msPerHour = msPerMinute * 60;
    const msPerDay = msPerHour * 24;
    const msPerMonth = msPerDay * 30;
    const msPerYear = msPerDay * 365;

    let elapsed = now.getTime() - date.getTime();

    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style });

    if (elapsed <= 0) {
      elapsed = 0;
    }

    if (elapsed < msPerMinute) {
      return rtf.format(-Math.floor(elapsed / 1000), 'seconds');
    }

    if (elapsed < msPerHour) {
      return rtf.format(-Math.floor(elapsed / msPerMinute), 'minutes');
    }

    if (elapsed < msPerDay) {
      return rtf.format(-Math.floor(elapsed / msPerHour), 'hours');
    }

    if (elapsed < msPerMonth) {
      return rtf.format(
        -Math.floor(
          (now.setHours(0, 0, 0, 0) - date.setHours(0, 0, 0, 0)) / msPerDay
        ),
        'days'
      );
    }

    if (elapsed < msPerYear) {
      return rtf.format(
        -Math.floor(
          (now.setHours(0, 0, 0, 0) - date.setHours(0, 0, 0, 0)) / msPerMonth
        ),
        'months'
      );
    }

    return new Date(date).toLocaleDateString(locale);
  }

  return undefined;
};

export const formatTimeSpanToTime = (
  value: string | null | undefined
): string => {
  if (value) {
    const data: { [key: string]: number } = {
      H: 0,
      M: 0,
      S: 0,
    };

    const parts = value.split(':');
    if (parts.length > 1) {
      data.H = parseInt(parts[0]);
      data.M = parseInt(parts[1]);
    } else {
      [...value.matchAll(/([\d]+)([H|M|S])/g)].forEach(option => {
        if (option[2]) {
          data[option[2]] = parseInt(option[1]);
        }
      });
    }

    const pad = (num: number) => num.toString().padStart(2, '0');

    return `${pad(data.H)}:${pad(data.M)}`;
  }

  return '';
};

export const formatTimeToTimeSpan = (
  value: string | null | undefined
): string => {
  if (value) {
    const pad = (num: string) =>
      parseInt(num ?? '0')
        .toString()
        .padStart(2, '0');
    const [h, m, s] = value.split(':');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  return '';
};

export const formatTimeToMinutes = (
  value: string | null | undefined
): number => {
  if (value) {
    const [hours, minutes] = value.split(':');

    return Number(hours) * 60 + Number(minutes);
  }

  return 0;
};

export const getTimeSpanMinutes = (value: string | null | undefined): number =>
  formatTimeToMinutes(formatTimeSpanToTime(value));

export const formatMinutesToTime = (
  value: number | null | undefined
): string => {
  if (value) {
    return `${Math.floor(value / 60)
      .toString()
      .padStart(2, '0')}:${(value % 60).toString().padStart(2, '0')}`;
  }

  return '00:00';
};

export const copyTimeFromDate = (fromDate: Date, toDate: Date) => {
  const pad = (num: number, digits = 2) => num.toString().padStart(digits, '0');

  const tzo = -fromDate.getTimezoneOffset();

  return DateTimeFormat.replace('YYYY', toDate.getFullYear().toString())
    .replace('mm', pad(toDate.getMonth() + 1))
    .replace('dd', pad(toDate.getDate()))
    .replace('HH', pad(fromDate.getHours()))
    .replace('ii', pad(fromDate.getMinutes()))
    .replace('ss', pad(fromDate.getSeconds()))
    .replace('Z', `${tzo >= 0 ? '+' : '-'}${pad(tzo / 60)}:${pad(tzo % 60)}`);
};

export const formatDateTimeToTime = (
  dateTime: Date | string | null | undefined,
  intl: IntlShape
) => {
  if (!dateTime) {
    return '';
  }

  const localTime = new Date(dateTime);
  if (!localTime) {
    return '';
  }
  return intl.formatTime(localTime, {
    hour: 'numeric',
    minute: 'numeric',
  });
};

export const removeTimezone = (value: string | null | undefined) => {
  if (value) {
    return new Date(value);
  }

  return null;
};

export const getWeekNumber = (date: Date) => {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const diffMillis = date.getTime() - startOfYear.getTime();
  const diffDays = Math.floor(diffMillis / (24 * 60 * 60 * 1000)) + 1;
  const weekNumber = Math.ceil(diffDays / 7);
  return weekNumber;
};

export const getDatesOverlap = (
  range1: { start: Date; end: Date },
  range2: { start: Date; end: Date }
) => range1.start <= range2.end && range1.end >= range2.start;

export const getYearsEqual = (date1: Date, date2: Date) =>
  date1.getFullYear() === date2.getFullYear();

export const getWeeksEqual = (date1: Date, date2: Date) =>
  getWeekNumber(date1) === getWeekNumber(date2);
